import { ChangeEvent, useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { SingleValue } from "react-select";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { banks } from "../../../../../../app/utils/bank";
import { accountType } from "../../../../../../app/utils/accountType";
import { FormCustomerData } from "../../useCustomerDetailsPageController";
import { OptionType } from "../../../../../../app/utils/optionSelectType";
import { customerUpdateData } from "../../../../../../app/services/customerService/customerUpdate";
import { customerService } from "../../../../../../app/services/customerService";
import { useToast } from "../../../../../components/ui/use-toast";
import { deformateDate } from "../../../../../../app/utils/deformateDate";
import { cepService } from "../../../../../../app/services/cepService";
import { BanksContext } from "../../../../../../app/contexts/banksContext";

export const useCustomerPerfilController = () => {
  const { id } = useParams();
  const methods = useFormContext<FormCustomerData>();
  const [value, setValue] = useState("");
  const [selectedBank, setSelectedBank] = useState<SingleValue<OptionType>>();
  const [selectedAccountType, setSelectedAccountType] =
    useState<SingleValue<OptionType>>();
  const [selectedTypeOfPixKey, setSelectedTypeOfPixKey] =
    useState<SingleValue<OptionType>>();
  const queryClient = useQueryClient();

  const { data } = useContext(BanksContext);

  const optionBanks = data?.banks ?? [];

  const handleAccountNumberBlur = (event: ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    newValue = newValue.replace(/\D/g, "");

    if (newValue.length > 1 && !isNaN(Number(newValue))) {
      newValue = newValue.slice(0, -1) + "-" + newValue.slice(-1);
    }

    setValue(newValue);
  };

  const handleCepRequest = async (cep: string) => {
    const response = await cepService(cep.replace(/\D/g, ""));

    if (response) {
      methods.setValue("address.city", response.localidade);
      methods.setValue("address.complement", response.complemento);
      methods.setValue("address.neighborhood", response.bairro);
      methods.setValue("address.state", response.uf);
      methods.setValue("address.street", response.logradouro);
    }
  };

  const handleSelectBank = (selectedBank: SingleValue<OptionType>) => {
    if (selectedBank !== null) {
      methods.setValue("bankAccount.bank", selectedBank.value.toString());
      setSelectedBank(selectedBank);
    }
  };

  const handleSelectAccountType = (
    selectedAccountType: SingleValue<OptionType>,
  ) => {
    if (selectedAccountType !== null) {
      methods.setValue(
        "bankAccount.accountType",
        selectedAccountType.value.toString(),
      );
      setSelectedAccountType(selectedAccountType);
    }
  };

  const handleSelectTypeOfPixKey = (
    selectedTypeOfPixKey: SingleValue<OptionType>,
  ) => {
    if (selectedTypeOfPixKey !== null) {
      methods.setValue(
        "bankAccount.pixType",
        selectedTypeOfPixKey.value.toString(),
      );
      setSelectedTypeOfPixKey(selectedTypeOfPixKey);
    }
  };
  const handleBank = (bankNumber: string) => {
    const userBank = banks.filter((bank) => bank.value === bankNumber);

    if (userBank.length > 0) {
      return userBank[0].label;
    } else {
      return bankNumber;
    }
  };

  const handleAccountType = (accountValueType: string) => {
    const userAccountType = accountType.filter(
      (account) => account.value === accountValueType,
    );

    if (userAccountType.length > 0) {
      return userAccountType[0].label;
    } else {
      return accountValueType;
    }
  };

  const { toast } = useToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      data,
      id,
    }: {
      data: customerUpdateData;
      id: string;
    }) => {
      return customerService.customerUpdate(data, id);
    },
  });
  const handleCustomerInfoSubmit = async (data: FormCustomerData) => {
    try {
      const removeEmptyFields = <T extends Record<string, unknown>>(
        obj: T,
      ): Partial<T> => {
        const result: Partial<T> = {};
        for (const key in obj) {
          const value = obj[key];
          if (value === "" || value === null || value === undefined) {
            continue;
          }
          if (typeof value === "object" && !Array.isArray(value)) {
            const cleaned = removeEmptyFields(value as Record<string, unknown>);
            if (Object.keys(cleaned).length > 0) {
              result[key] = cleaned as T[typeof key];
            }
          } else {
            result[key] = value;
          }
        }
        return result;
      };

      const formattedData = { ...data };

      if (data.birthDate) {
        formattedData.birthDate = deformateDate(data.birthDate);
      } else {
        delete formattedData.birthDate;
      }
      formattedData.phone = data.phone?.replace(/\D/g, "");
      if (data.address?.zipCode) {
        formattedData.address.zipCode = data.address.zipCode.replace(/\D/g, "");
      }
      if (data.bankAccount?.account) {
        formattedData.bankAccount.account = data.bankAccount.account.replace(
          /\D/g,
          "",
        );
      }

      const cleanedData = removeEmptyFields(
        formattedData,
      ) as customerUpdateData;

      if (id) {
        const response = await mutateAsync({ data: cleanedData, id });

        if (response.name === data.name) {
          queryClient.invalidateQueries({
            queryKey: ["customerInfo", id],
          });

          toast({
            title: "Dados atualizados com sucesso!",
          });
        }
      }
    } catch (error) {
      console.log(error);
      toast({
        variant: "destructive",
        title:
          "Ocorreu um erro ao tentar atualizar os dados, tente novamente mais tarde.",
      });
    }
  };
  return {
    methods,
    handleCustomerInfoSubmit,
    isPending,
    handleBank,
    handleAccountType,
    selectedBank,
    selectedAccountType,
    selectedTypeOfPixKey,
    handleSelectBank,
    handleSelectTypeOfPixKey,
    handleSelectAccountType,
    handleAccountNumberBlur,
    value,
    setValue,
    handleCepRequest,
    optionBanks,
  };
};
